import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { SET_MODES_PRIORITY_STRING } from '../ARSConstants';
import ARSSidePanel from './ARSSidepanel';
import ComputedSubstitutionComponent from 'Pages/GMS/ComputedSubstitutions/pages/ComputedSubstitutionView';
import RuleView from 'Pages/GMS/Rules/RuleView';
import { updateRecommenderModel } from '../ARSServices/ARSServices';
import { ArsContext } from '../Contexts/ArsContext';
import HotSeller from './HotSeller/HotSeller';
import Seasonal from './Seasonal/Seasonal';
import {
    TSetPriorityMode,
    TSubmodel,
    TCombineModesConfig,
    TSetModePriority,
    ModeSelectionType
} from '../Types';
import CuratedCategoryRelevancyTable from 'Pages/GMS/CategoryRelevancy/components/CuratedCategoryRelevancyTable';
import { CategoryRelevancyProvider } from 'Pages/GMS/CategoryRelevancy/contexts/CategoryRelevancyContext';

import ModeSelectionDropdown from './ModeSelectionDropdown';
import CombinedModesView from './CombinedModesView';
import IndividualModesView from './IndividualModesView';
import { convertToCombinedMode, convertToIndividualMode, getDefaultCombineModesConfig, getFallbackModes, getIndividualModes, getPrimaryModes, isCombinedModeEnabled, isSetModePriorityArray, isSetModePriorityObject } from '../ModeTransformUtils';


type ArsModelDetailsProps = {
    setModePriority: TSetModePriority;
    combineModesConfig: TCombineModesConfig;
    updateSetModePriority: (setModePriority: TSetModePriority) => void;
    updateCombineModesConfig: (config: TCombineModesConfig) => void;
    activeSubmodel: TSubmodel;
    isDisabled?: boolean;
    onExpertCuratedToggle?: (isEnabled: boolean) => void;
};

const ArsModelDetails = ({
    setModePriority,
    combineModesConfig,
    updateSetModePriority,
    updateCombineModesConfig,
    activeSubmodel,
    isDisabled = false,
    onExpertCuratedToggle
}: ArsModelDetailsProps) => {
    const [showSidePanel, setShowSidePanel] = useState<boolean>(false);
    const [modeKey, setModeKey] = useState<string>('');
    const [isFormValid, setIsFormValid] = useState<boolean>(true);
    const { model } = useContext(ArsContext);

    const isCombinedMode = isCombinedModeEnabled(combineModesConfig);

    // Initialize: Convert array to object format if combine mode is enabled but data is in array format
    useEffect(() => {
        if (isCombinedMode && isSetModePriorityArray(setModePriority)) {
            const convertedModes = convertToCombinedMode(setModePriority);
            updateSetModePriority(convertedModes);
        }
    }, [isCombinedMode, setModePriority]);

    // Handle target categories enabled: disable expert_curated_coseller only
    useEffect(() => {
        if (!isDisabled) return;

        // Disable expert_curated_coseller in Combined mode
        if (isSetModePriorityObject(setModePriority)) {
            const expertCuratedIndex = setModePriority.primary.findIndex(
                (mode) => mode.mode === 'expert_curated_coseller'
            );
            if (
                expertCuratedIndex !== -1 &&
                setModePriority.primary[expertCuratedIndex].active
            ) {
                const updatedPrimary = [...setModePriority.primary];
                updatedPrimary[expertCuratedIndex] = {
                    ...updatedPrimary[expertCuratedIndex],
                    active: false
                };
                updateSetModePriority({
                    ...setModePriority,
                    primary: updatedPrimary
                });
                onExpertCuratedToggle?.(false);
            }
        }

        // Disable expert_curated_coseller in Individual mode
        if (isSetModePriorityArray(setModePriority)) {
            const expertCuratedIndex = setModePriority.findIndex(
                (mode) => mode.mode === 'expert_curated_coseller'
            );
            if (
                expertCuratedIndex !== -1 &&
                setModePriority[expertCuratedIndex].active
            ) {
                const updatedModes = [...setModePriority];
                updatedModes[expertCuratedIndex] = {
                    ...updatedModes[expertCuratedIndex],
                    active: false
                };
                updateSetModePriority(updatedModes);
                onExpertCuratedToggle?.(false);
            }
        }
    }, [isDisabled, setModePriority]);

    const handleModeSelectionChange = (newMode: ModeSelectionType) => {
        const newEnabled = newMode === 'combine';

        if (newEnabled && isSetModePriorityArray(setModePriority)) {
            const convertedModes = convertToCombinedMode(setModePriority);
            updateSetModePriority(convertedModes);
            updateCombineModesConfig(getDefaultCombineModesConfig(true));
        } else if (!newEnabled && isSetModePriorityObject(setModePriority)) {
            const convertedModes = convertToIndividualMode(setModePriority);
            updateSetModePriority(convertedModes);
            updateCombineModesConfig({ enabled: false });
        } else if (!newEnabled && isSetModePriorityArray(setModePriority)) {
            // Backend returned array format, just update config to disable combine mode
            updateCombineModesConfig({ enabled: false });
        } else if (newEnabled && isSetModePriorityObject(setModePriority)) {
            // Already in object format, just update config to enable combine mode
            updateCombineModesConfig(getDefaultCombineModesConfig(true));
        }
    };

    const handleConfigChange = (configUpdate: Partial<TCombineModesConfig>) => {
        updateCombineModesConfig({
            ...combineModesConfig,
            ...configUpdate
        });
    };

    const handlePrimaryModesChange = (modes: TSetPriorityMode[]) => {
        if (isSetModePriorityObject(setModePriority)) {
            const modeChanged = modes.find((mode, index) => {
                const originalMode = setModePriority.primary[index];
                return (
                    mode.mode === 'expert_curated_coseller' &&
                    mode.active !== originalMode?.active
                );
            });

            if (modeChanged) {
                onExpertCuratedToggle?.(modeChanged.active);
            }

            updateSetModePriority({
                ...setModePriority,
                primary: modes
            });
        }
    };

    const handleFallbackModesChange = (modes: TSetPriorityMode[]) => {
        if (isSetModePriorityObject(setModePriority)) {
            updateSetModePriority({
                ...setModePriority,
                fallback: modes
            });
        }
    };

    const handleIndividualModesChange = (modes: TSetPriorityMode[]) => {
        const modeChanged = modes.find((mode, index) => {
            const originalModes = getIndividualModes(setModePriority);
            const originalMode = originalModes[index];
            return (
                mode.mode === 'expert_curated_coseller' &&
                mode.active !== originalMode?.active
            );
        });

        if (modeChanged) {
            onExpertCuratedToggle?.(modeChanged.active);
        }

        updateSetModePriority(modes);
    };

    const handleValidationChange = (isValid: boolean) => {
        setIsFormValid(isValid);
    };

    const onCloseSidePanel = () => {
        if ((modeKey === 'seasonal' || modeKey === 'hot_seller') && !isFormValid) {
            toast.error('Please set both start and end dates for Custom Date selection');
            return;
        }

        setShowSidePanel(false);
        setModeKey('');
    };

    const handleSettingsClick = async (modeKeyParam: string) => {
        if (!model) return;

        const hasValidTitles = model?.submodel?.every((x) => {
            const config = x.combine_modes_config ?? { enabled: false };
            if (isCombinedModeEnabled(config)) {
                const primaryTitleValid =
                    (config.primary_mode_title?.trim().length ?? 0) > 0;
                const fallbackTitleValid =
                    (config.fallback_mode_title?.trim().length ?? 0) > 0;
                return primaryTitleValid && fallbackTitleValid;
            }
            if (isSetModePriorityArray(x.setModePriority)) {
                return x.setModePriority.every((mode) => mode.title?.trim().length > 0);
            }
            return true;
        });

        if (!hasValidTitles) {
            toast.error(
                'All mode titles are required. Please ensure each mode has a valid title.'
            );
            return;
        }

        const updateModelResponse = await updateRecommenderModel(model);
        if (updateModelResponse?.done) {
            setModeKey(modeKeyParam);
            setShowSidePanel(true);
            setIsFormValid(true);
        } else {
            toast.error(updateModelResponse?.message ?? 'Failed to update the model');
        }
    };

    const getSidePanelHeader = (): string => {
        switch (modeKey) {
            case 'seasonal':
                return 'Seasonal';
            case 'hot_seller':
                return 'Hot Seller';
            case 'compatible_coseller':
            case 'pure_compatibility':
                return 'Configure Rules';
            case 'expert_curated_coseller':
                return 'Curated Categories';
            default:
                return 'Configure Pivots';
        }
    };

    const getSidePanelWidth = (): number => {
        return ['hot_seller', 'seasonal'].includes(modeKey) ? 25 : 70;
    };

    const renderSidePanelContent = () => {
        switch (modeKey) {
            case 'expert_curated_coseller':
                return (
                    <CategoryRelevancyProvider initialTab="curated" showTabSelector={false}>
                        <CuratedCategoryRelevancyTable />
                    </CategoryRelevancyProvider>
                );
            case 'compatible_coseller':
                return (
                    <RuleView
                        mode={modeKey}
                        key={`rule-view-${activeSubmodel.submodel_id}`}
                        baseCategories={
                            activeSubmodel?.categories?.length
                                ? activeSubmodel.categories
                                : undefined
                        }
                        targetCategories={
                            activeSubmodel?.target_categories?.length
                                ? activeSubmodel.target_categories
                                : undefined
                        }
                    />
                );
            case 'pure_compatibility':
                return (
                    <RuleView
                        mode={modeKey}
                        key={`pure-compatibility-rule-view-${activeSubmodel.submodel_id}`}
                        baseCategories={
                            activeSubmodel?.categories?.length
                                ? activeSubmodel.categories
                                : undefined
                        }
                        targetCategories={
                            activeSubmodel?.target_categories?.length
                                ? activeSubmodel.target_categories
                                : undefined
                        }
                    />
                );
            case 'computed_substitution':
                return (
                    <ComputedSubstitutionComponent
                        isSidePanel={true}
                        key={`substitution-view-${activeSubmodel.submodel_id}`}
                        baseCategories={
                            activeSubmodel?.categories?.length
                                ? activeSubmodel.categories
                                : undefined
                        }
                    />
                );
            case 'hot_seller':
                return (
                    <HotSeller
                        key={`hot-seller-view-${activeSubmodel.submodel_id}`}
                        setModePriority={
                            isCombinedMode
                                ? getFallbackModes(setModePriority)
                                : getIndividualModes(setModePriority)
                        }
                        updateSetModePriority={(modes) => {
                            if (isCombinedMode) {
                                handleFallbackModesChange(modes);
                            } else {
                                handleIndividualModesChange(modes);
                            }
                        }}
                        onValidationChange={handleValidationChange}
                    />
                );
            case 'seasonal':
                return (
                    <Seasonal
                        key={`seasonal-view-${activeSubmodel.submodel_id}`}
                        setModePriority={
                            isCombinedMode
                                ? getFallbackModes(setModePriority)
                                : getIndividualModes(setModePriority)
                        }
                        updateSetModePriority={(modes) => {
                            if (isCombinedMode) {
                                handleFallbackModesChange(modes);
                            } else {
                                handleIndividualModesChange(modes);
                            }
                        }}
                        onValidationChange={handleValidationChange}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div
            className="tw-min-h-[calc(100vh-171px)] tw-border tw-border-[#dadbdd] tw-mt-3 tw-ml-2 tw-rounded-sm tw-bg-transparent"
            data-testid="ars-model-details"
        >
            <div className="tw-w-full tw-p-5 tw-pt-2 tw-bg-white tw-rounded-tl tw-rounded-tr">
                <span className='tw-font-bold tw-text-[#6d788c]'>
                    {SET_MODES_PRIORITY_STRING}
                </span>
                <div className='tw-mt-2.5'>
                    <ModeSelectionDropdown
                        value={isCombinedMode ? 'combine' : 'individual'}
                        onChange={handleModeSelectionChange}
                        id="ars-mode-selection"
                    />
                </div>
            </div>

            <div className="tw-px-4">
                <div className="tw-mt-4 tw-mb-2">
                    {isCombinedMode ? (
                        <CombinedModesView
                            config={combineModesConfig}
                            primaryModes={getPrimaryModes(setModePriority)}
                            fallbackModes={getFallbackModes(setModePriority)}
                            onConfigChange={handleConfigChange}
                            onPrimaryModesChange={handlePrimaryModesChange}
                            onFallbackModesChange={handleFallbackModesChange}
                            onSettingsClick={handleSettingsClick}
                            disabled={isDisabled}
                            id="ars-combined-modes"
                        />
                    ) : (
                        <IndividualModesView
                            modes={getIndividualModes(setModePriority)}
                            onModesChange={handleIndividualModesChange}
                            onSettingsClick={handleSettingsClick}
                            disabled={isDisabled}
                            id="ars-individual-modes"
                        />
                    )}
                </div>
            </div>

            {showSidePanel && (
                <ARSSidePanel
                    show={showSidePanel}
                    close={onCloseSidePanel}
                    header={getSidePanelHeader()}
                    overlayWidthInPercentage={getSidePanelWidth()}
                >
                    {renderSidePanelContent() || <></>}
                </ARSSidePanel>
            )}
        </div>
    );
};

export default ArsModelDetails;